/**
 * Artists — curated scene pages (public /artists/:slug).
 * Collection id `artists` is the product name. Radio ingest stays hubAudioTracks.
 */

import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage, isFirestoreAvailable, isStorageAvailable } from '../../firebase';
import { isReservedUsername } from '../userProfile';

/** Firestore collection name — wire value for this product. */
export const ARTISTS_COLLECTION = 'artists';
/** Firestore collection name — slug → artistId uniqueness index. */
export const ARTIST_SLUG_INDEX_COLLECTION = 'artistSlugIndex';

export const ARTIST_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
export const ARTIST_PHOTO_TYPES = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

export const ARTIST_COPY_FIELDS = Object.freeze([
  'bio',
  'photoURL',
  'instagramHandle',
  'websiteUrl',
  'spotifyUrl',
  'bandcampUrl',
  'genre',
]);

const NAME_MAX = 80;
const BIO_MAX = 2000;
const GENRE_MAX = 40;
const URL_MAX = 300;
const SLUG_MAX = 60;

function requireFirestore() {
  if (!isFirestoreAvailable() || !db) {
    throw new Error('Firestore is not available.');
  }
}

function newArtistId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `artist-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function clip(value, max) {
  return String(value || '').trim().slice(0, max);
}

function clipUrl(value) {
  const raw = clip(value, URL_MAX);
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return '';
}

/**
 * @param {string} name
 * @returns {string}
 */
export function slugifyArtistName(name) {
  const base = String(name || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX);
  if (!base) return '';
  if (isReservedUsername(base)) {
    const suffixed = `${base}-music`.slice(0, SLUG_MAX);
    return isReservedUsername(suffixed) ? '' : suffixed;
  }
  return base;
}

/**
 * @param {string} slug
 * @returns {boolean}
 */
export function isValidArtistSlug(slug) {
  const key = String(slug || '').trim();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(key)) return false;
  if (key.length < 2 || key.length > SLUG_MAX) return false;
  if (isReservedUsername(key)) return false;
  return true;
}

/**
 * @param {unknown} ids
 * @returns {string[]}
 */
export function normalizeArtistIds(ids) {
  if (!Array.isArray(ids)) return [];
  const seen = new Set();
  const out = [];
  ids.forEach((raw) => {
    const id = String(raw || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

/**
 * @param {string[]} ids
 * @param {Array<{ id: string, name?: string, slug?: string }>} artists
 * @returns {Array<{ id: string, name: string, slug: string }>}
 */
export function normalizeArtistLineup(ids, artists) {
  const byId = new Map((artists || []).map((a) => [a.id, a]));
  return normalizeArtistIds(ids)
    .map((id) => {
      const hit = byId.get(id);
      if (!hit) return null;
      const slug = String(hit.slug || '').trim();
      const name = String(hit.name || '').trim();
      if (!slug || !name) return null;
      return { id, name, slug };
    })
    .filter(Boolean);
}

/**
 * @param {object|null|undefined} data
 * @param {string} id
 * @returns {object|null}
 */
export function normalizeArtist(data, id) {
  if (!data || typeof data !== 'object') return null;
  const slug = String(data.slug || '').trim();
  const name = String(data.name || '').trim();
  if (!id || !slug || !name) return null;
  return {
    id: String(id),
    name,
    slug,
    bio: String(data.bio || '').trim(),
    photoURL: data.photoURL ? String(data.photoURL) : '',
    instagramHandle: String(data.instagramHandle || '').trim(),
    websiteUrl: String(data.websiteUrl || '').trim(),
    spotifyUrl: String(data.spotifyUrl || '').trim(),
    bandcampUrl: String(data.bandcampUrl || '').trim(),
    genre: String(data.genre || '').trim(),
    published: data.published === true,
    claimedByUid: data.claimedByUid ? String(data.claimedByUid) : '',
  };
}

/**
 * @param {string} slug
 * @returns {string}
 */
export function artistDetailPath(slug) {
  const key = String(slug || '').trim();
  if (!key) return '/artists';
  return `/artists/${encodeURIComponent(key)}`;
}

/**
 * @param {Array<{ published?: boolean }>} rows
 * @returns {typeof rows}
 */
export function filterPublishedArtists(rows) {
  return (rows || []).filter((row) => row && row.published === true);
}

/**
 * @param {Array<{ artistId?: string }>} tracks
 * @param {string} artistId
 * @returns {typeof tracks}
 */
export function tracksForArtist(tracks, artistId) {
  const id = String(artistId || '').trim();
  if (!id) return [];
  return (tracks || []).filter((track) => String(track?.artistId || '') === id);
}

/**
 * @param {Array<{ artistIds?: string[] }>} events
 * @param {string} artistId
 * @returns {typeof events}
 */
export function eventsForArtist(events, artistId) {
  const id = String(artistId || '').trim();
  if (!id) return [];
  return (events || []).filter((event) =>
    normalizeArtistIds(event?.artistIds).includes(id)
  );
}

/**
 * Claimed users may only patch these fields.
 * @param {object} fields
 * @returns {object}
 */
export function claimedArtistCopyPayload(fields) {
  const out = {};
  ARTIST_COPY_FIELDS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(fields || {}, key)) return;
    if (key === 'photoURL' || key.endsWith('Url')) {
      out[key] = key === 'photoURL'
        ? clip(fields[key], URL_MAX)
        : clipUrl(fields[key]);
      return;
    }
    if (key === 'bio') {
      out[key] = clip(fields[key], BIO_MAX);
      return;
    }
    if (key === 'genre') {
      out[key] = clip(fields[key], GENRE_MAX);
      return;
    }
    if (key === 'instagramHandle') {
      out[key] = clip(fields[key], NAME_MAX).replace(/^@/, '');
      return;
    }
    out[key] = clip(fields[key], NAME_MAX);
  });
  return out;
}

function artistWritePayload(input) {
  const name = clip(input.name, NAME_MAX);
  if (!name) throw new Error('Name is required.');
  const slug = isValidArtistSlug(input.slug)
    ? String(input.slug).trim()
    : slugifyArtistName(name);
  if (!isValidArtistSlug(slug)) {
    throw new Error('Pick a different slug.');
  }
  const claimedByUid = String(input.claimedByUid || '').trim();
  return {
    name,
    slug,
    bio: clip(input.bio, BIO_MAX),
    photoURL: clip(input.photoURL, URL_MAX),
    instagramHandle: clip(input.instagramHandle, NAME_MAX).replace(/^@/, ''),
    websiteUrl: clipUrl(input.websiteUrl),
    spotifyUrl: clipUrl(input.spotifyUrl),
    bandcampUrl: clipUrl(input.bandcampUrl),
    genre: clip(input.genre, GENRE_MAX),
    published: input.published === true,
    claimedByUid: claimedByUid || deleteField(),
  };
}

/**
 * @returns {Promise<object[]>}
 */
export async function listArtists() {
  requireFirestore();
  const snap = await getDocs(collection(db, ARTISTS_COLLECTION));
  return snap.docs
    .map((d) => normalizeArtist(d.data(), d.id))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * @returns {Promise<object[]>}
 */
export async function listPublishedArtists() {
  requireFirestore();
  const snap = await getDocs(
    query(collection(db, ARTISTS_COLLECTION), where('published', '==', true))
  );
  return snap.docs
    .map((d) => normalizeArtist(d.data(), d.id))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * @param {string} artistId
 * @returns {Promise<object|null>}
 */
export async function getArtistById(artistId) {
  requireFirestore();
  const id = String(artistId || '').trim();
  if (!id) return null;
  const snap = await getDoc(doc(db, ARTISTS_COLLECTION, id));
  if (!snap.exists()) return null;
  return normalizeArtist(snap.data(), snap.id);
}

/**
 * @param {string} slug
 * @returns {Promise<object|null>}
 */
export async function getArtistBySlug(slug) {
  requireFirestore();
  const key = String(slug || '').trim();
  if (!key) return null;
  const indexSnap = await getDoc(doc(db, ARTIST_SLUG_INDEX_COLLECTION, key));
  if (!indexSnap.exists()) return null;
  const artistId = String(indexSnap.data()?.artistId || '').trim();
  if (!artistId) return null;
  return getArtistById(artistId);
}

/**
 * @param {string} uid
 * @returns {Promise<object|null>}
 */
export async function getArtistByClaimedUid(uid) {
  requireFirestore();
  const owner = String(uid || '').trim();
  if (!owner) return null;
  const snap = await getDocs(
    query(
      collection(db, ARTISTS_COLLECTION),
      where('claimedByUid', '==', owner),
      limit(1)
    )
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return normalizeArtist(d.data(), d.id);
}

async function assertClaimAvailable(uid, artistId) {
  const owner = String(uid || '').trim();
  if (!owner) return;
  const snap = await getDocs(
    query(
      collection(db, ARTISTS_COLLECTION),
      where('claimedByUid', '==', owner),
      limit(2)
    )
  );
  const clash = snap.docs.find((d) => d.id !== artistId);
  if (clash) {
    throw new Error('That user already claims another artist.');
  }
}

/**
 * Admin create/update. Returns the saved artist.
 * @param {object} input
 * @param {{ uid?: string }} [opts]
 */
export async function saveArtist(input, { uid } = {}) {
  requireFirestore();
  const id = String(input.id || '').trim() || newArtistId();
  const payload = artistWritePayload(input);
  const claimedByUid = String(input.claimedByUid || '').trim();
  if (claimedByUid) {
    await assertClaimAvailable(claimedByUid, id);
  }

  let previousSlug = '';
  if (input.id) {
    const existing = await getArtistById(id);
    previousSlug = existing?.slug || '';
  }

  if (payload.slug !== previousSlug) {
    const taken = await getDoc(doc(db, ARTIST_SLUG_INDEX_COLLECTION, payload.slug));
    if (taken.exists() && String(taken.data()?.artistId || '') !== id) {
      throw new Error('That slug is already used.');
    }
  }

  const batch = writeBatch(db);
  const artistRef = doc(db, ARTISTS_COLLECTION, id);
  const body = {
    ...payload,
    updatedAt: serverTimestamp(),
    updatedBy: uid || null,
  };
  if (!input.id) {
    body.createdAt = serverTimestamp();
  }
  batch.set(artistRef, body, { merge: true });
  batch.set(doc(db, ARTIST_SLUG_INDEX_COLLECTION, payload.slug), { artistId: id });
  if (previousSlug && previousSlug !== payload.slug) {
    batch.delete(doc(db, ARTIST_SLUG_INDEX_COLLECTION, previousSlug));
  }
  await batch.commit();

  if (previousSlug && (previousSlug !== payload.slug || input.name)) {
    await syncHubTracksForArtist({
      id,
      name: payload.name,
      slug: payload.slug,
    });
  }

  return getArtistById(id);
}

/**
 * Claimed user copy edit (no slug / publish / claim changes).
 * @param {string} artistId
 * @param {object} fields
 */
export async function updateClaimedArtistCopy(artistId, fields) {
  requireFirestore();
  const id = String(artistId || '').trim();
  if (!id) throw new Error('Missing artist.');
  const patch = claimedArtistCopyPayload(fields);
  if (Object.keys(patch).length === 0) {
    throw new Error('Nothing to save.');
  }
  patch.updatedAt = serverTimestamp();
  await updateDoc(doc(db, ARTISTS_COLLECTION, id), patch);
  return getArtistById(id);
}

/**
 * @param {string} artistId
 * @param {File|Blob} file
 * @returns {Promise<string>}
 */
export async function uploadArtistPhoto(artistId, file) {
  if (!isStorageAvailable() || !storage) {
    throw new Error('Storage is not available.');
  }
  const id = String(artistId || '').trim();
  if (!id) throw new Error('Missing artist.');
  if (!file) throw new Error('Choose a photo to upload.');
  if (!ARTIST_PHOTO_TYPES.includes(file.type)) {
    throw new Error('Use a JPG, PNG, or WebP photo.');
  }
  if (file.size > ARTIST_PHOTO_MAX_BYTES) {
    throw new Error('Photo must be under 2 MB.');
  }
  const ext =
    file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
  const storageRef = ref(storage, `artist-photos/${id}/avatar.${ext}`);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
}

async function syncHubTracksForArtist(artist) {
  /** Firestore collection name — wire value unchanged (MapToBe Radio). */
  const hubAudioCollection = 'hubAudioTracks';
  const snap = await getDocs(
    query(
      collection(db, hubAudioCollection),
      where('artistId', '==', artist.id)
    )
  );
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach((d) => {
    batch.update(d.ref, {
      artistName: artist.name,
      artistSlug: artist.slug,
    });
  });
  await batch.commit();
}
